import * as S from './styles';
import * as Base from '../styles';
import { Frame } from 'dragonmouse-shared/interfaces';
import FrameDetailsSection from '../FrameDetailsSection';
import Thumbnail, { ThumbnailSize } from 'components/Widget/Thumbnail';
import MarkdownText from 'components/Widget/MarkdownText';
import { MenuItemAction } from 'menu/MenuItem';
import { showContextMenu } from 'redux/mode/actions';
import { useDispatch } from 'react-redux';

interface FrameMetadataProps {
	frame: Frame | undefined;
	hiddenProperties?: string[];
}

const FrameMetadata = ({ frame, hiddenProperties = [] }: FrameMetadataProps) => {
	const dispatch = useDispatch();

	const onContextMenu = (e: React.MouseEvent) => {
		// You may wonder to yourself:
		// "what if the user right-clicks on something OTHER than the selection? -- wouldn't that triger the below code?"
		// In practice, right-clicking clears the selection. So the below code is triggered
		// only when you right-click on a text selection within the element this handler is attached to.
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	const toTitleCase = (str: string) => {
		return str.replace(/\b\w/g, (char) => char.toUpperCase());
	};

	if (!frame) return null;

	// take the display labels and sort them so that these keys appear first:
	// 'prompt'', or anything with "reference" in it
	const displayLabels = frame.metadata.displayLabels
		? Object.entries(frame.metadata.displayLabels).sort((a, b) => {
				if (a[0] === 'prompt' || a[0].includes('reference')) return -1;
				if (b[0] === 'prompt' || b[0].includes('reference')) return 1;
				return 0;
		  })
		: [];

	// we want to display chat references differently from other display labels,
	// so break them out into their own variable
	const chatReference = displayLabels.find(([key]) => key === 'chat reference');
	const designNotes = displayLabels.find(([key]) => key === 'design notes');

	const filteredDisplayLabels = displayLabels.filter(
		([key]) =>
			key !== 'chat reference' &&
			key !== 'design notes' &&
			key !== 'device' &&
			// this is currently set for imported images - and not needed as we show dimensions elsewhere
			key !== 'original size'
	);

	return (
		<S.Base>
			{designNotes && designNotes[1] ? (
				<S.DesignNotes>
					<MarkdownText onContextMenu={onContextMenu}>{String(designNotes[1])}</MarkdownText>
				</S.DesignNotes>
			) : (
				<S.DesignNotesSpacer />
			)}
			{chatReference && chatReference[1] && (
				<FrameDetailsSection
					label="Source Chat Reference"
					key={`source-chat-${frame.metadata.sourceChatId || 'reference'}`}
				>
					<Base.Row>
						<S.PropertyValue onContextMenu={onContextMenu}>
							{String(chatReference[1])}
						</S.PropertyValue>
					</Base.Row>
				</FrameDetailsSection>
			)}
			{frame.metadata?.type === 'enhance' && chatReference && (
				<S.DesignNotes>
					<MarkdownText onContextMenu={onContextMenu}>
						Variation of the reference frame.
					</MarkdownText>
				</S.DesignNotes>
			)}
			{frame.metadata.sourceFrameIds && frame.metadata.sourceFrameIds.length > 0 && (
				<FrameDetailsSection
					label={'Reference Frame' + (frame.metadata.sourceFrameIds.length > 1 ? 's' : '')}
					key={`source-${frame.metadata.sourceFrameIds.join('-')}`}
				>
					<Base.Row>
						{frame.metadata.sourceFrameIds?.map((sourceFrameId, index) => (
							<Thumbnail
								key={`source-${index}`}
								frameId={sourceFrameId}
								tooltip="This frame was used as a source."
								size={ThumbnailSize.RIGHT_PANEL}
							/>
						))}
					</Base.Row>
					<Base.Spacer />
				</FrameDetailsSection>
			)}
			{filteredDisplayLabels?.length > 0 &&
				filteredDisplayLabels.map(
					([key, value]) =>
						!hiddenProperties.includes(key) &&
						value &&
						String(value).trim() !== '' && (
							<FrameDetailsSection label={toTitleCase(String(key))} key={`display-label-${key}`}>
								<S.PropertyValue onContextMenu={onContextMenu}>{String(value)}</S.PropertyValue>
							</FrameDetailsSection>
						)
				)}
		</S.Base>
	);
};

export default FrameMetadata;
